<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubSheet from './Sheet.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

// A LISTAGEM de Promoções aberta DENTRO de um modal — o mesmo desenho da loja no
// StoreSheet. A ROTA /promotions CONTINUA existindo e igual: aqui
// reaproveitamos a mesma `<BasePromotionsCore />`, em modo embutido
// (`embedded`), que é o que a impede de ler `route.params`, de assinar o SEO e
// de navegar quando o jogador abre uma promoção — lista e promoção acontecem no
// MESMO painel, e o voltar da tela devolve pra listagem.
//
// Quem abre é o deep-link `?open=promotions` (ou `?open=promotion&slug=…`, que
// já cai na promoção). Os cartões do hub seguem como estavam: eles abrem a
// promoção específica no PostSheet.
//
// Sem `title` no casco: a Core já traz o BaseScreenHeader dela.
//
// Estado só em memória (nada na URL), igual roletinha e cashback.
const store = useLoyaltyHubStore();
const { isPromotionsOpen, promotionSlug } = storeToRefs(store);

// Monta só depois do slide-up: a listagem busca a categoria inteira do CMS e
// montar durante a animação faz a subida engasgar. Mesma estratégia da loja.
const mounted = ref(false);

const sheetRef = ref<InstanceType<typeof LoyaltyHubSheet> | null>(null);

// Fechar e reabrir DURANTE o slide-down cancela a saída: o `@closed`
// (after-leave) nunca dispara, o `mounted` não volta pra false e a tela
// continuaria montada com a promoção anterior aberta. A chave por abertura força
// a remontagem, que é o que devolve o painel pra listagem.
const openCount = ref(0);

watch(isPromotionsOpen, (open) => {
  if (open) openCount.value += 1;
});

function close() {
  store.closePromotions();
}

// Troca de tela dentro do painel: quem rola é o sheet, então é ele que sobe.
function onNavigate() {
  sheetRef.value?.scrollToTop();
}
</script>

<template>
  <LoyaltyHubSheet
    ref="sheetRef"
    desktop-max-width="1000px"
    :open="isPromotionsOpen"
    @close="close"
    @closed="mounted = false"
    @opened="mounted = true"
  >
    <!-- Wrapper meu, e NÃO `class` direto na `<BasePromotionsCore>`: raiz de
         fragmento não herda classe — mesmo caso do StoreSheet. -->
    <div
      v-if="mounted"
      class="lhpr-promotions"
    >
      <BasePromotionsCore
        :key="openCount"
        embedded
        :slug="promotionSlug"
        @close="close"
        @navigate="onNavigate"
      />
    </div>

    <!-- Enquanto o painel sobe, o vão fica com o esqueleto da lista. -->
    <div
      v-else
      class="lhpr-loading"
    >
      <div
        v-for="i in 3"
        :key="`lhpr-skeleton-${i}`"
        class="lhpr-skeleton"
      ></div>
    </div>
  </LoyaltyHubSheet>
</template>

<style scoped lang="scss">
// A tela se desenha pra ser a PÁGINA: ela sobe o próprio topo (`-mt-4 lg:-mt-5`)
// pra compensar a folga do header do site, que aqui não existe. O alvo é por
// posição (`> *:first-child`), e não pela classe, pelo mesmo motivo do
// StoreSheet.
.lhpr-promotions {
  width: 100%;

  :deep(> *:first-child) {
    margin-top: 0 !important;
  }
}

.lhpr-loading {
  display: grid;
  gap: 12px;
}

.lhpr-skeleton {
  @apply w-full h-40 rounded-lg bg-texts/5 border border-solid border-texts/10;
}
</style>
